import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend.skiesseven.com';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationError, setVerificationError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      // Try to get client_reference_id from URL parameter first, then sessionStorage
      let clientReferenceId = searchParams.get('client_reference_id');
      
      if (!clientReferenceId) {
        clientReferenceId = sessionStorage.getItem('payment_client_reference');
      }

      if (!clientReferenceId) {
        setVerificationError('Unable to verify payment - missing reference information');
        setErrorDetails({
          type: 'missing_reference',
          message: 'No client reference ID found in URL or session storage'
        });
        setIsVerifying(false);
        return;
      }

      console.log('=== PAYMENT VERIFICATION ===');
      console.log('Client Reference ID:', clientReferenceId);

      try {
        // Directly verify payment by client_reference_id (matches backend route)
        const verifyUrl = `${BACKEND_URL}/api/verify-thawani-payment-by-reference/${encodeURIComponent(clientReferenceId)}`;
        console.log('Verify URL:', verifyUrl);
        
        const verifyResponse = await fetch(verifyUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        // Clone the response so we can inspect raw text in case JSON parsing fails
        const responseClone = verifyResponse.clone();

        let verifyData;
        try {
          verifyData = await verifyResponse.json();
          console.log('Verification Response:', verifyData);

          setDebugInfo({
            clientReferenceId,
            sessionId: verifyData.session_id,
            verifyData
          });
        } catch (parseError) {
          console.error('Failed to parse verification response:', parseError);
          const rawBody = await responseClone.text().catch(() => '<unreadable body>');
          console.error('Raw verification response body:', rawBody);

          setDebugInfo({
            clientReferenceId,
            rawBody
          });

          throw new Error('Server returned invalid response');
        }

        if (!verifyResponse.ok) {
          console.error('Verification failed:', verifyData);
          setErrorDetails({
            status: verifyResponse.status,
            message: verifyData.error || 'Verification failed',
            fullError: verifyData
          });
          throw new Error(verifyData.error || 'Payment verification failed');
        }

        // Check payment status
        console.log('Payment Status:', verifyData.payment_status);
        console.log('Success Flag:', verifyData.success);

        if (verifyData.payment_status === 'pending') {
          setVerificationError('Payment is still being processed. Your registration will be confirmed once payment is completed.');
          setErrorDetails({
            status: 'pending',
            message: 'Payment is pending confirmation from the payment gateway.'
          });
          setIsVerifying(false);
        } else if (verifyData.success === true || verifyData.payment_status === 'paid') {
          // Payment successful!
          console.log('✓ Payment verified successfully');
          
          // Clear stored reference
          sessionStorage.removeItem('payment_client_reference');
          
          setIsVerifying(false);
        } else {
          console.warn('Unexpected payment status:', verifyData.payment_status);
          setVerificationError(`Unexpected payment status: ${verifyData.payment_status}. Please contact support.`);
          setErrorDetails({
            status: 'unexpected',
            message: `Status: ${verifyData.payment_status}`
          });
          setIsVerifying(false);
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          setVerificationError('Unable to connect to the server. Please check your internet connection and try again.');
          setErrorDetails({
            type: 'network',
            message: error.message
          });
        } else {
          setVerificationError(error.message || 'Failed to verify payment. Please contact support.');
          if (!errorDetails) {
            setErrorDetails({
              type: 'unknown',
              message: error.message
            });
          }
        }
        setIsVerifying(false);
      }
    };

    verifyPayment();
    // We intentionally only depend on searchParams so verification runs once
    // for the given URL parameters and doesn't loop when errorDetails changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div className="min-h-screen pt-32 pb-24">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="glass-effect border border-accent/30 p-12 text-center"
          data-testid="payment-success-page"
        >
          {isVerifying ? (
            <>
              <Loader2 className="h-16 w-16 text-accent mx-auto mb-8 animate-spin" />
              <h1 className="text-4xl md:text-5xl font-heading font-black mb-6">Verifying Payment...</h1>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Please wait while we confirm your payment and complete your registration.
              </p>
            </>
          ) : verificationError ? (
            <>
              <div className="inline-flex p-6 border-4 border-destructive/30 rounded-full mb-8">
                <svg className="h-16 w-16 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-black mb-6">Verification Issue</h1>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {verificationError}
              </p>
              
              {/* Debug info */}
              {debugInfo && (
                <div className="text-xs text-muted-foreground mb-4 p-4 bg-muted rounded text-left">
                  <strong>Debug Info:</strong>
                  <pre className="mt-2 whitespace-pre-wrap overflow-auto max-h-96">
                    {JSON.stringify(debugInfo, null, 2)}
                  </pre>
                </div>
              )}
              
              {errorDetails && (
                <div className="text-xs text-muted-foreground mb-4 p-4 bg-muted rounded text-left">
                  <strong>Error Details:</strong>
                  <pre className="mt-2 whitespace-pre-wrap">{JSON.stringify(errorDetails, null, 2)}</pre>
                </div>
              )}
              
              <p className="text-sm text-muted-foreground mb-8">
                If you completed the payment, your registration may still be processing. Please contact support if you have any concerns.
              </p>
              <div className="space-y-4">
                <Link
                  to="/"
                  className="block w-full text-center px-8 py-4 bg-primary text-background hover:bg-primary/90 font-bold uppercase tracking-widest transition-all duration-300 neon-glow rounded"
                >
                  Return to Home and retry verification
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="inline-flex p-6 border-4 border-accent rounded-full mb-8">
                <svg className="h-16 w-16 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-black mb-6">Payment Successful!</h1>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Thank you for your purchase. Your registration is confirmed and you will receive a confirmation email shortly with your ticket details and event information.
              </p>
              <div className="space-y-4">
                <Link
                  to="/"
                  data-testid="success-home-btn"
                  className="block w-full text-center px-8 py-4 bg-primary text-background hover:bg-primary/90 font-bold uppercase tracking-widest transition-all duration-300 neon-glow rounded"
                >
                  Return to Home
                </Link>
                <Link
                  to="/#schedule"
                  data-testid="success-schedule-btn"
                  className="block w-full text-center px-8 py-4 border border-primary/50 text-primary hover:bg-primary/10 font-bold uppercase tracking-widest transition-all duration-300 rounded"
                >
                  View Event Schedule
                </Link>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSuccess;